import { useEffect, useRef, useState, VideoHTMLAttributes } from "react";

type Props = VideoHTMLAttributes<HTMLVideoElement> & {
  src: string;
  rootMargin?: string;
};

/**
 * Video that defers loading until it enters (or nears) the viewport.
 * Use for any non-hero video to keep initial page weight low.
 */
export const LazyVideo = ({ src, rootMargin = "300px", ...rest }: Props) => {
  const ref = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || shouldLoad) return;
    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShouldLoad(true);
          io.disconnect();
        }
      },
      { rootMargin }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldLoad, rootMargin]);

  return (
    <video
      ref={ref}
      preload="none"
      {...rest}
      src={shouldLoad ? src : undefined}
    />
  );
};

export default LazyVideo;
